import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import FbButton from 'components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { Tabs } from 'components/FbUI/Tabs'
import { Select } from 'components/UI/Select/Select'
import { useMemo, useState, useCallback } from 'react'
import { useUnifiedSearchCompanyUrl } from 'utils/getUnifiedSearchCompanyUrl'
import { CSVRow, InternalColumns } from '../importer-types'
import { useImporterViewModel, getColumnOption } from '../ImporterViewModel'
import { OppTypeBadge, getOppTypeLabel } from './OppTypeBadge'
import { CompanyActionCell } from './CompanyActionCell'
import {
  setAsExcludedBulk,
  setAsCustomCompanyBulk,
} from '../importDataTransforms'
import { useContainerHeight } from './useContainerHeight'

type FinishTab = 'matched' | 'unmatched'

export function ImportWizardFinishMatchingStep({
  show,
  data,
  skipMatching = false,
}: {
  show: boolean
  data: CSVRow[]
  skipMatching?: boolean
}) {
  const viewModel = useImporterViewModel()
  const { getCompanyUrlFromId } = useUnifiedSearchCompanyUrl()
  const [tab, setTab] = useState<FinishTab>('unmatched')
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [tableContainerRef, tableContainerHeight] = useContainerHeight()

  // Identify company_match columns from headers
  const companyMatchHeaders = useMemo(() => {
    return (
      viewModel.importData?.headers.filter(
        (h: { original: string; mapped: string }) =>
          getColumnOption(h.mapped)?.type === 'company_match'
      ) ?? []
    )
  }, [viewModel.importData?.headers])

  // Split data into matched and unmatched
  const { matchedData, unmatchedData } = useMemo(() => {
    const matched: CSVRow[] = []
    const unmatched: CSVRow[] = []
    data.forEach((row) => {
      const chain = row[InternalColumns.MATCHED_CHAIN]
      const door = row[InternalColumns.MATCHED_DOOR]
      if (chain || door) {
        matched.push(row)
      } else {
        unmatched.push(row)
      }
    })
    return { matchedData: matched, unmatchedData: unmatched }
  }, [data])

  const currentData = tab === 'matched' ? matchedData : unmatchedData

  // Get selected row hashes for bulk actions
  const selectedHashes = useMemo(() => {
    return Object.entries(rowSelection)
      .filter(([, selected]) => selected)
      .map(([hash]) => hash)
  }, [rowSelection])

  const selectedCount = selectedHashes.length

  // Bulk action handlers
  const handleBulkExclude = useCallback(() => {
    if (selectedHashes.length === 0) return
    viewModel.updateImportData((data) =>
      setAsExcludedBulk(data, selectedHashes)
    )
    setRowSelection({})
  }, [selectedHashes, viewModel])

  const handleBulkMarkAsCustom = useCallback(
    (categoryId: number, subcategoryId: number) => {
      if (selectedHashes.length === 0) return
      viewModel.updateImportData((data) =>
        setAsCustomCompanyBulk(data, selectedHashes, categoryId, subcategoryId)
      )
      setRowSelection({})
    },
    [selectedHashes, viewModel]
  )

  const columnDefs: ColumnDef<CSVRow>[] = useMemo(() => {
    const columns: ColumnDef<CSVRow>[] = []

    // Input Data column
    columns.push({
      id: 'input_data',
      header: 'Your Data',
      accessorFn: (row) => {
        return companyMatchHeaders
          .map((h: { original: string }) => row[h.original] || '')
          .join(' ')
          .toLowerCase()
      },
      cell: ({ row }) => {
        const items: { label: string; value: string }[] = []

        companyMatchHeaders.forEach(
          (header: { original: string; mapped: string }) => {
            const value = row.original[header.original]
            if (value) {
              items.push({
                label: getColumnOption(header.mapped)?.name || header.mapped,
                value,
              })
            }
          }
        )

        return (
          <div className="space-y-0.5 text-sm">
            {items.map((item, idx) => (
              <div key={idx} className="text-fg">
                <span className="font-medium text-fg-secondary">
                  {item.label}:
                </span>{' '}
                {item.value}
              </div>
            ))}
          </div>
        )
      },
    })

    // Matched Chain and Door columns (for matched tab)
    if (tab === 'matched') {
      columns.push({
        id: 'matched_chain',
        header: 'Matched Chain',
        accessorFn: (row) => {
          const chain = row[InternalColumns.MATCHED_CHAIN]
          return (chain?.name || '').toLowerCase()
        },
        cell: ({ row }) => {
          const chain = row.original[InternalColumns.MATCHED_CHAIN]
          if (!chain) return <span className="text-sm text-fg-muted">-</span>

          const chainUrl = getCompanyUrlFromId(`${chain.type}${chain.item_id}`)

          return (
            <div className="space-y-0.5 text-sm">
              <div className="text-fg">
                <span className="font-medium text-fg-secondary">Name:</span>{' '}
                {chainUrl ? (
                  <FbLink to={chainUrl} target="_blank">
                    {chain.name}
                  </FbLink>
                ) : (
                  chain.name
                )}
              </div>
              <div className="text-fg">
                <span className="font-medium text-fg-secondary">Domain:</span>{' '}
                {chain.domain || '-'}
              </div>
              <div
                className="text-fg truncate max-w-xs"
                title={chain.city_summary || '-'}
              >
                <span className="font-medium text-fg-secondary">Cities:</span>{' '}
                {chain.city_summary || '-'}
              </div>
              <div
                className="text-fg truncate max-w-xs"
                title={chain.state_summary || '-'}
              >
                <span className="font-medium text-fg-secondary">States:</span>{' '}
                {chain.state_summary || '-'}
              </div>
            </div>
          )
        },
      })

      columns.push({
        id: 'matched_door',
        header: 'Matched Door',
        accessorFn: (row) => {
          const door = row[InternalColumns.MATCHED_DOOR]
          return (door?.name || '').toLowerCase()
        },
        cell: ({ row }) => {
          const door = row.original[InternalColumns.MATCHED_DOOR]
          if (!door) return <span className="text-sm text-fg-muted">-</span>

          const doorUrl = getCompanyUrlFromId(`${door.type}${door.item_id}`)

          return (
            <div className="space-y-0.5 text-sm">
              <div className="text-fg">
                <span className="font-medium text-fg-secondary">Name:</span>{' '}
                {doorUrl ? (
                  <FbLink to={doorUrl} target="_blank">
                    {door.name}
                  </FbLink>
                ) : (
                  door.name
                )}
              </div>
              <div className="text-fg">
                <span className="font-medium text-fg-secondary">Address:</span>{' '}
                {door.address || '-'}
              </div>
              <div className="text-fg">
                <span className="font-medium text-fg-secondary">City:</span>{' '}
                {door.city || '-'}
              </div>
              <div className="text-fg">
                <span className="font-medium text-fg-secondary">State:</span>{' '}
                {door.state || '-'}
              </div>
              <div className="text-fg">
                <span className="font-medium text-fg-secondary">Country:</span>{' '}
                {door.country || '-'}
              </div>
            </div>
          )
        },
      })

      // Company Target Type column (for matched tab)
      columns.push({
        id: 'company_target_type',
        header: 'Company Type',
        accessorFn: (row) => {
          const chain = row[InternalColumns.MATCHED_CHAIN]
          const door = row[InternalColumns.MATCHED_DOOR]
          const typeCode = door?.type || chain?.type
          return getOppTypeLabel(typeCode) || ''
        },
        cell: ({ row }) => {
          const chain = row.original[InternalColumns.MATCHED_CHAIN]
          const door = row.original[InternalColumns.MATCHED_DOOR]
          const typeCode = door?.type || chain?.type
          if (!typeCode) return '-'
          return <OppTypeBadge typeCode={typeCode} />
        },
      })
    }

    // Action column for unmatched tab
    if (tab === 'unmatched') {
      columns.push({
        id: 'action',
        header: 'Action',
        cell: ({ row }) => <CompanyActionCell row={row.original} compact />,
        size: 220,
      })
    }

    return columns
  }, [companyMatchHeaders, getCompanyUrlFromId, tab])

  if (!show) {
    return null
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {skipMatching ? (
        // Skip matching mode: Show all companies with per-row action dropdowns
        <>
          <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex-shrink-0">
            <p className="text-sm text-purple-800">
              <strong>Skip Matching:</strong> Set each company&apos;s action
              below. Default is Exclude.
            </p>
          </div>

          {/* Bulk action bar */}
          {selectedCount > 0 && (
            <div className="flex-shrink-0">
              <BulkActionBar
                selectedCount={selectedCount}
                onExclude={handleBulkExclude}
                onMarkAsCustom={handleBulkMarkAsCustom}
                companySubcategories={viewModel.companySubcategories}
              />
            </div>
          )}

          <div ref={tableContainerRef} className="flex-1 min-h-0">
            <ClientDataTable
              height={tableContainerHeight || 400}
              data={data}
              columns={columnDefs}
              enableSorting
              enableRowSelection
              selectedRows={rowSelection}
              onRowSelectionChange={setRowSelection}
              selectionRowID={InternalColumns.COMPANY_HASH as keyof CSVRow}
            />
          </div>
        </>
      ) : (
        // Normal mode: Show matched/unmatched tabs
        <>
          <div className="flex-shrink-0">
            <Tabs
              tabs={[
                {
                  key: 'unmatched',
                  title: `Unmatched (${unmatchedData.length})`,
                },
                {
                  key: 'matched',
                  title: `Matched (${matchedData.length})`,
                },
              ]}
              selectedTab={tab}
              setSelectedTab={(t) => {
                setTab(t)
                setRowSelection({}) // Clear selection when switching tabs
              }}
            />
          </div>

          {/* Bulk action bar for unmatched tab */}
          {tab === 'unmatched' && selectedCount > 0 && (
            <div className="flex-shrink-0">
              <BulkActionBar
                selectedCount={selectedCount}
                onExclude={handleBulkExclude}
                onMarkAsCustom={handleBulkMarkAsCustom}
                companySubcategories={viewModel.companySubcategories}
              />
            </div>
          )}

          <div ref={tableContainerRef} className="flex-1 min-h-0">
            <ClientDataTable
              height={tableContainerHeight || 400}
              data={currentData}
              columns={columnDefs}
              enableSorting
              enableRowSelection={tab === 'unmatched'}
              selectedRows={tab === 'unmatched' ? rowSelection : {}}
              onRowSelectionChange={
                tab === 'unmatched' ? setRowSelection : undefined
              }
              selectionRowID={InternalColumns.COMPANY_HASH as keyof CSVRow}
            />
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Bulk action bar for setting selected rows as excluded or marking as custom company type
 */
function BulkActionBar({
  selectedCount,
  onExclude,
  onMarkAsCustom,
  companySubcategories,
}: {
  selectedCount: number
  onExclude: () => void
  onMarkAsCustom: (categoryId: number, subcategoryId: number) => void
  companySubcategories?: Array<{
    category_id: number
    value: number
    category_name: string
    subcategory_name: string
  }>
}) {
  const customTypeOptions = useMemo(
    () =>
      companySubcategories?.map((subcat) => ({
        label: `${subcat.category_name} / ${subcat.subcategory_name}`,
        value: `${subcat.category_id}-${subcat.value}`,
      })) ?? [],
    [companySubcategories]
  )

  const handleCustomTypeSelect = useCallback(
    (value: string) => {
      const [categoryId, subcategoryId] = value.split('-').map(Number)
      onMarkAsCustom(categoryId, subcategoryId)
    },
    [onMarkAsCustom]
  )

  return (
    <div className="flex items-center gap-3 p-3 mb-2 bg-blue-50 border border-blue-200 rounded-lg">
      <span className="text-sm font-medium text-blue-800">
        {selectedCount} selected
      </span>
      <FbButton variant="secondary" size="sm" onClick={onExclude}>
        Set Exclude
      </FbButton>
      {customTypeOptions.length > 0 && (
        <Select
          items={customTypeOptions}
          onSelect={handleCustomTypeSelect}
          buttonLabel="Mark as Custom"
          buttonVariant="secondary"
          buttonSize="sm"
        />
      )}
    </div>
  )
}
